import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { useNavigate } from "react-router-dom";
import { authService, type LoginRequest } from "../services";
import { useAuthStore } from "../store";
import { useTaskStore, type Task } from "../store/task.store";
import { setAuthToken } from "../lib";
import { getDefaultRoute } from "../data/taskNavigation";

function resolveSavedTasks(
  permittedTasks: Task[],
  savedTaskIds: string[] | null | undefined
): Task[] {
  if (!savedTaskIds || savedTaskIds.length === 0) {
    return [];
  }
  const savedIdSet = new Set(savedTaskIds);
  return permittedTasks.filter((t) => savedIdSet.has(t.id));
}

export function useAuth() {
  const profile = useAuthStore((state) => state.profile);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoading = useAuthStore((state) => state.isLoading);

  return {
    user: profile,
    isAuthenticated,
    isLoading,
  };
}

export function useLogin() {
  const { login } = useAuthStore();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data: LoginRequest) => authService.login(data),
    onSuccess: (response) => {
      setAuthToken(response.token);
      const permittedTasks = response.user.permittedTasks || [];
      const roleTaskIds = response.user.roleTaskIds || [];
      const savedTaskIds = response.user.savedTaskIds ?? null;

      const taskStore = useTaskStore.getState();
      taskStore.setPermittedTasks(permittedTasks);
      taskStore.setRoleTaskIds(roleTaskIds);

      const validSavedTasks = resolveSavedTasks(permittedTasks, savedTaskIds);
      taskStore.setSelectedTasks(validSavedTasks);

      login(response.token, {
        id: response.user.id,
        username: response.user.username,
        email: response.user.email,
        fullName: response.user.fullName,
        roleId: response.user.roleId || undefined,
        roleName: response.user.roleName,
        groups: response.user.groups || [],
        groupId: response.user.groupId || undefined,
        groupName: response.user.groupName,
        facilityId: response.user.facilityId || undefined,
        facilityName: response.user.facilityName,
        isActive: response.user.isActive,
        isRemote: response.user.isRemote,
        permittedTasks,
        permissions: response.user.permissions || [],
      });

      if (response.mustChangePassword) {
        navigate("/change-password");
        return;
      }

      if (validSavedTasks.length > 0) {
        navigate(getDefaultRoute(validSavedTasks.map((t) => t.slug)));
      } else {
        navigate("/select-tasks");
      }
    },
    onError: (error: any) => {
      notifications.show({
        title: "Login Failed",
        message: error.response?.data?.message || "Invalid credentials",
        color: "red",
      });
    },
  });
}

export function useCurrentUser() {
  const { isAuthenticated, setProfile } = useAuthStore();

  return useQuery({
    queryKey: ["current-user"],
    queryFn: async () => {
      const data = await authService.getCurrentUser();
      const permittedTasks = data.permittedTasks || [];
      const roleTaskIds = data.roleTaskIds || [];
      const savedTaskIds = data.savedTaskIds ?? null;
      setProfile({
        id: data.id,
        username: data.username,
        email: data.email,
        fullName: data.fullName,
        roleId: data.roleId || undefined,
        roleName: data.roleName,
        groups: data.groups || [],
        groupId: data.groupId || undefined,
        groupName: data.groupName,
        facilityId: data.facilityId || undefined,
        facilityName: data.facilityName,
        isActive: data.isActive,
        isRemote: data.isRemote,
        permittedTasks,
        permissions: data.permissions || [],
      });
      const taskStore = useTaskStore.getState();
      taskStore.setPermittedTasks(permittedTasks);
      taskStore.setRoleTaskIds(roleTaskIds);

      const validSavedTasks = resolveSavedTasks(permittedTasks, savedTaskIds);
      if (validSavedTasks.length > 0) {
        taskStore.setSelectedTasks(validSavedTasks);
      } else if (taskStore.selectedTasks.length > 0) {
        const stillValid = taskStore.selectedTasks.filter((t) =>
          permittedTasks.some((p) => p.id === t.id)
        );
        if (stillValid.length === 0) {
          taskStore.setSelectedTasks([]);
        } else if (stillValid.length !== taskStore.selectedTasks.length) {
          taskStore.setSelectedTasks(stillValid);
        }
      }
      return data;
    },
    enabled: isAuthenticated,
    staleTime: 0,
  });
}

export function useLogout() {
  const { logout } = useAuthStore();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return () => {
    setAuthToken(null);
    useTaskStore.getState().clearTaskSelection();
    logout();
    queryClient.clear();
    navigate("/login");
  };
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (data: { oldPassword: string; newPassword: string }) =>
      authService.changePassword(data),
    onSuccess: () => {
      notifications.show({
        title: "Success",
        message: "Password changed successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.message || "Failed to change password",
        color: "red",
      });
    },
  });
}
